import { FunctionComponent, useEffect, useState } from 'react';
import { Headline, Div, BodyText, Icon, Title } from '@gaiads/telia-react-component-library';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  OrganizationAssetCard,
  OrganizationAssetCardTranslations,
  createTicket,
  AssetAssignToUser,
  AssetAssignToUserTranslations,
  AssetAssignToOrganization,
  UserDetails,
  EolFlowFinishFromChoice,
  EndOfLeaseChoiceTranslations,
  AssetAssignToOrganizationTranslations,
} from '@cdaas/reusable-components-daas';
import {
  AssetEndOfLeaseRequest,
  InvoicingAccountOptionResponseContents,
  InvoicingAccountResponse,
  OrganizationAssetResponse,
  UserAssetResponse,
  UserRole,
  UserWithOrganizationAndUserGroupResponse,
} from '@cdaas/api-clients/dist/asset-user-openapi';

import {
  assignAssetToOrganization,
  assignAssetToUser,
  confirmOrCancelEndOfLeaseDecision,
  getAsset as getAssetApi,
  getAUser,
  getInvoicingAccounts,
  getReturnPackageOrder,
  getTicketURI,
  getUsersAllAssets as getUsersAllAssetsApi,
  getUsersFromOrganizationOrUserGroup,
  hasUserARequiredRole,
  postEndOfLeaseDecision,
  renderContactSupportText,
  updateOrganizationAsset,
} from '../../utils';
import { refreshUserInfo, shopSetOrderForOrganization, shopSetOrderOnBehalfUser, showGrowlNotification } from '../../store/actions';
import { RootState } from '../../store/reducers';
import { User } from '../../types';
import CustomLoader from '../../Components/CustomLoader/CustomLoader';
import { COUNTRY_CALLING_CODES } from '../../utils/constants';

const OrganizationAssetDetails: FunctionComponent = () => {
  const [asset, setAsset] = useState<OrganizationAssetResponse>();
  const [assetUser, setAssetUser] = useState<UserWithOrganizationAndUserGroupResponse>();
  const [userAssets, setUserAssets] = useState<UserAssetResponse[]>([]);

  const loggedInUser = useSelector((state: RootState) => state.authentication.user);
  const organization = useSelector((state: RootState) => state.authentication.user?.organization);
  const useBuyoutWithPriceFlow = useSelector((state: RootState) => state.featureFlags.FEATURE_FLAG_FRONTEND_FLOW_BUYOUT_WITH_PRICE_ENABLED);
  const recurringMonthlyExtensionNoEnabled = useSelector(
    (state: RootState) => state.featureFlags.FEATURE_FLAG_RECURRING_MONTHLY_EXTENSION_NO_ENABLED,
  );

  const [assignToDifferentUserModalOpen, setAssignToDifferentUserModalOpen] = useState(false);
  const [assignToOrganizationModalOpen, setAssignToOrganizationModalOpen] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState<User[]>([]);
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState(false);
  const [invoicingAccountsLoading, setInvoicingAccountsLoading] = useState(true);
  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [assetAssignLoading, setAssetAssignLoading] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const dispatch = useDispatch();
  const assetId = useParams<{ id: string }>().id;
  const history = useHistory();
  const { t } = useTranslation('assets');
  const { t: tGlobal } = useTranslation('globalComponents');
  const { t: tShop } = useTranslation('shop');
  const { t: tErrors } = useTranslation('apiErrors');

  const automaticExtension: boolean = asset?.prices?.lease?.can_lease_be_auto_extended ?? false;

  const contractEndsTitleMessage = t(
    recurringMonthlyExtensionNoEnabled && automaticExtension
      ? 'AssetCard.endOfLeaseNotifications.contractEndsTitleUpgrade'
      : 'AssetCard.endOfLeaseNotifications.contractEndsTitle',
  );

  const decisionNotYetDoneMessage = t(
    automaticExtension
      ? recurringMonthlyExtensionNoEnabled
        ? 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionRecurringAdmin'
        : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageExtensionAdmin'
      : 'AssetCard.endOfLeaseNotifications.decisionNotYetDoneMessageLeaseEnd',
  );

  const daysLeftForDecisionMessage = t(
    recurringMonthlyExtensionNoEnabled
      ? automaticExtension
        ? 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionRecurring'
        : 'AssetCard.endOfLeaseNotifications.daysLeftForDecisionLast'
      : 'AssetCard.endOfLeaseNotifications.daysLeftForDecision',
  );

  const translations: OrganizationAssetCardTranslations = {
    validUntil: t('AssetCard.validUntil'),
    monthShort: t('AssetCard.monthShort', { count: 1 }),
    monthLeft: t('AssetCard.monthLeft'),
    monthsLeft: t('AssetCard.monthsLeft'),
    vatInfo: t('AssetCard.vatIncluded'),
    replacedDeviceTag: t('AssetCard.replacedDeviceTag'),
    contractEndedDate: t('AssetCard.contractEndedDate'),
    deviceDetails: t('AssetCard.deviceDetails'),
    deviceBought: t('AssetCard.eolMadeDecision.deviceBought'),
    deviceNotYetReturned: t('AssetCard.eolMadeDecision.deviceNotYetReturned'),
    deviceReturned: t('AssetCard.eolMadeDecision.deviceReturned'),
    deviceNotReturned: t('AssetCard.eolMadeDecision.deviceNotReturned'),
    location: t('AssetCard.location'),
    locationReference: t('AssetCard.locationReference'),
    invoicingAccount: t('AssetCard.invoicingAccount'),
    activeInvoicingAccount: t('AssetCard.activeInvoicingAccount'),
    user: t('AssetCard.user'),
    userName: t('AssetCard.userName'),
    phoneNumber: t('AssetCard.phoneNumber'),
    otherDevices: t('AssetCard.otherDevices'),
    events: t('AssetCard.events'),
    tickets: t('AssetCard.tickets'),
    createTicketButton: t('AssetCard.createTicketButton'),
    createSecondTicketButton: t('AssetCard.createSecondTicketButton'),
    secondTicketModal: {
      title: t('AssetCard.secondTicketModal.title'),
      button: t('AssetCard.secondTicketModal.button'),
    },
    imeiNumber: t('AssetCard.imeiNumber'),
    previousImeis: t('AssetCard.previousImeis'),
    sku: t('AssetCard.sku'),
    bundleAccessories: t('AssetCard.bundleAccessories'),
    bundleOrderId: t('AssetCard.bundleOrderId'),
    bundleServices: t('AssetCard.bundleServices'),
    insurance: t('AssetCard.insurance'),
    ticketStatus: t('AssetCard.ticketStatus'),
    ticketOpen: t('AssetCard.ticketOpen'),
    ticketClosed: t('AssetCard.ticketClosed'),
    link: t('AssetCard.link'),
    contractEnded: t('AssetCard.contractEnded'),
    contractExtended: t('AssetCard.contractExtended'),
    endOfLeaseSelected: t('AssetCard.endOfLeaseSelected'),
    returnEnvelopeSent: t('AssetCard.returnEnvelopeSent'),
    endOfLeaseReturnColtHardwareDeliveryId: t('AssetCard.endOfLeaseReturnColtHardwareDeliveryId'),
    months: t('AssetCard.months'),
    delivered: t('AssetCard.delivered'),
    shipped: t('AssetCard.shipped'),
    endOfLeaseNotificationTranslations: {
      contractEndsTitle: contractEndsTitleMessage,
      decisionNotYetDoneMessage: decisionNotYetDoneMessage,
      daysLeftForDecision: daysLeftForDecisionMessage,
      buyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitleAdmin'),
      buyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessageAdmin'),
      returnTitle: t('AssetCard.endOfLeaseNotifications.returnTitleAdmin'),
      returnMessage: t('AssetCard.endOfLeaseNotifications.returnMessageAdmin'),
    },
    assignToDifferentUser: t('AssetCard.assignToDifferentUser'),
    assignToOrganization: t('AssetCard.assignToOrganization'),
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
    inactiveAssetInvoicingAccount: t('AssetCard.inactiveAssetInvoicingAccount'),
    eolSelectedBuyout: t('AssetCard.eolActionEventMessage.buyoutSelectedEvent'),
    eolSelectedReturn: t('AssetCard.eolActionEventMessage.returnDeviceEvent'),
    eolSelectedNotReturned: t('AssetCard.eolActionEventMessage.notReturnedEvent'),
    invoicingAccountSelection: {
      dropdownLabel: t('AssetCard.invoicingAccountSelection.dropdownLabel'),
      clearSelection: t('AssetCard.invoicingAccountSelection.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.invoicingAccountSelection.noOptionsFoundInfo'),
      helperText: t('AssetCard.invoicingAccountSelection.helperText'),
      errorMessage: t('AssetCard.invoicingAccountSelection.errorMessage'),
      accountNotValidTitle: t('AssetCard.invoicingAccountSelection.accountNotValidTitle'),
      accountNotValidMessage: t('AssetCard.invoicingAccountSelection.accountNotValidMessage'),
    },
  };

  const assignToUserTranslations: AssetAssignToUserTranslations = {
    deviceToBeAssigned: {
      name: t('AssetCard.assign.deviceToBeAssigned.name'),
      price: t('AssetCard.assign.deviceToBeAssigned.price'),
      currentUser: t('AssetCard.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('AssetCard.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    selectUserInput: {
      label: t('AssetCard.assign.selectUserInput.label'),
      placeholder: t('AssetCard.assign.selectUserInput.placeholder'),
    },
    userToAssignDeviceTo: {
      nameAndGroup: t('AssetCard.assign.userToAssignDeviceTo.nameAndGroup'),
      contact: t('AssetCard.assign.userToAssignDeviceTo.contact'),
      deviceLimit: t('AssetCard.assign.userToAssignDeviceTo.deviceLimit'),
      maxPricePaidByEmployer: t('AssetCard.assign.userToAssignDeviceTo.maxPricePaidByEmployer'),
    },
    newInvoicingAccount: {
      label: t('AssetCard.assign.newInvoicingAccount.label'),
      dropdownLabel: t('AssetCard.assign.newInvoicingAccount.dropdownLabelUser'),
      clearSelection: t('AssetCard.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('AssetCard.assign.newInvoicingAccount.helperTextUser'),
    },
    vatInfo: t('AssetCard.assign.vatIncluded'),
    month: t('AssetCard.assign.month'),
    months: t('AssetCard.assign.months'),
    unlimited: t('AssetCard.assign.unlimited'),
    leaseContinueInfo: {
      part1: t('AssetCard.assign.leaseContinueInfo.part1'),
      part2: t('AssetCard.assign.leaseContinueInfo.part2'),
    },
    modalHeading: t('AssetCard.assign.modalHeadingUser'),
    saveButton: t('AssetCard.assign.saveButton'),
    cancelButton: t('AssetCard.assign.cancelButton'),
    errors: {
      userDetailLoadingApiError: t('AssetCard.assign.errors.userDetailLoadingApiError'),
      cantBeAssignedBecauseOfSingleDevicePolicyError: t('AssetCard.assign.errors.cantBeAssignedBecauseOfSingleDevicePolicyError'),
      assignApiError: t('AssetCard.assign.errors.assignApiErrorUser'),
    },
    confirmationModal: {
      title: t('AssetCard.assign.confirmationModal.titleUser'),
      confirmInfo: t('AssetCard.assign.confirmationModal.confirmInfoUser'),
      confirmList: {
        device: t('AssetCard.assign.confirmationModal.confirmList.device'),
        price: t('AssetCard.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
        allowance: {
          allowance: t('AssetCard.assign.confirmationModal.confirmList.allowance.allowance'),
          possibleSalaryDeduction: t('AssetCard.assign.confirmationModal.confirmList.allowance.possibleSalaryDeduction'),
        },
        deviceLimit: t('AssetCard.assign.confirmationModal.confirmList.deviceLimit'),
      },
      vatInfo: t('AssetCard.assign.confirmationModal.vatIncluded'),
      month: t('AssetCard.assign.confirmationModal.month'),
      months: t('AssetCard.assign.confirmationModal.months'),
      confirmButton: t('AssetCard.assign.confirmationModal.confirmButton'),
      cancelButton: t('AssetCard.assign.confirmationModal.cancelButton'),
    },
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
  };

  const assignToOrganizationTranslations: AssetAssignToOrganizationTranslations = {
    deviceToBeAssigned: {
      name: t('AssetCard.assign.deviceToBeAssigned.name'),
      price: t('AssetCard.assign.deviceToBeAssigned.price'),
      currentUser: t('AssetCard.assign.deviceToBeAssigned.currentUser'),
      currentInvoicingAccount: t('AssetCard.assign.deviceToBeAssigned.currentInvoicingAccount'),
    },
    newInvoicingAccount: {
      label: t('AssetCard.assign.newInvoicingAccount.label'),
      dropdownLabel: t('AssetCard.assign.newInvoicingAccount.dropdownLabelOrganization'),
      clearSelection: t('AssetCard.assign.newInvoicingAccount.clearSelection'),
      noOptionsFoundInfo: t('AssetCard.assign.newInvoicingAccount.noOptionsFoundInfo'),
      helperText: t('AssetCard.assign.newInvoicingAccount.helperTextOrganization'),
    },
    vatInfo: t('AssetCard.assign.vatIncluded'),
    month: t('AssetCard.assign.month'),
    months: t('AssetCard.assign.months'),
    unlimited: t('AssetCard.assign.unlimited'),
    leaseContinueInfo: {
      part1: t('AssetCard.assign.leaseContinueInfo.part1'),
      part2: t('AssetCard.assign.leaseContinueInfo.part2'),
    },
    modalHeading: t('AssetCard.assign.modalHeadingOrganization'),
    saveButton: t('AssetCard.assign.saveButton'),
    cancelButton: t('AssetCard.assign.cancelButton'),
    errors: {
      assignApiError: t('AssetCard.assign.errors.assignApiErrorOrganization'),
    },
    confirmationModal: {
      title: t('AssetCard.assign.confirmationModal.titleOrganization'),
      confirmInfo: t('AssetCard.assign.confirmationModal.confirmInfoOrganization'),
      confirmList: {
        device: t('AssetCard.assign.confirmationModal.confirmList.device'),
        price: t('AssetCard.assign.confirmationModal.confirmList.price'),
        remainingLeasePeriod: {
          remainingLeasePeriod: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.remainingLeasePeriod'),
          outOf: t('AssetCard.assign.confirmationModal.confirmList.remainingLeasePeriod.outOf'),
        },
      },
      vatInfo: t('AssetCard.assign.confirmationModal.vatIncluded'),
      month: t('AssetCard.assign.confirmationModal.month'),
      months: t('AssetCard.assign.confirmationModal.months'),
      confirmButton: t('AssetCard.assign.confirmationModal.confirmButton'),
      cancelButton: t('AssetCard.assign.confirmationModal.cancelButton'),
    },
    companyDevice: t('companyDevice'),
    inactiveUser: t('inactiveUser'),
  };

  const endOfLeaseChoiceTranslations: EndOfLeaseChoiceTranslations = {
    buyoutDeviceOptionTitle: t('AssetCard.assetActions.purchaseDeviceOption'),
    buyoutDeviceOptionText: t('AssetCard.assetActions.purchaseDeviceText'),
    buyoutDeviceOptionWithoutPriceButton: t('AssetCard.assetActions.purchaseDeviceButton'),
    buyoutDeviceOptionWithPricePurchaseButton: t('AssetCard.assetActions.purchaseButton'),
    buyoutDeviceOptionWithPriceAskForPriceButton: t('AssetCard.assetActions.askForPriceButton'),
    buyoutDeviceOptionWithPricePurchaseAnywayButton: t('AssetCard.assetActions.purchaseAnywayButton'),
    buyoutDeviceOptionWithPriceSideLabel: t('AssetCard.assetActions.purchaseDevicePriceLabelReusable'),
    buyoutDeviceOptionWithPriceWaitingForPrice: t('AssetCard.assetActions.waitingForPrice'),
    buyoutDeviceWithPriceWaitingPriceNotificationTitle: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceTitle'),
    buyoutDeviceWithPriceWaitingPriceNotificationText: t('AssetCard.endOfLeaseNotifications.waitingForBuyoutPriceMessage'),
    buyoutDeviceWithPriceEolNotificationContractEndsTitle: t('AssetCard.endOfLeaseNotifications.contractEndsTitle'),
    buyoutDeviceWithPriceEolNotificationDecisionNotYetDoneMessage: decisionNotYetDoneMessage,
    buyoutDeviceWithPriceEolNotificationDaysLeftForDecision: t('AssetCard.endOfLeaseNotifications.daysLeftForDecision'),
    buyoutDeviceWithPriceEolNotificationBuyoutTitle: t('AssetCard.endOfLeaseNotifications.buyoutTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationBuyoutMessage: t('AssetCard.endOfLeaseNotifications.buyoutMessageAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnTitle: t('AssetCard.endOfLeaseNotifications.returnTitleAdmin'),
    buyoutDeviceWithPriceEolNotificationReturnMessage: t('AssetCard.endOfLeaseNotifications.returnMessageAdmin'),
    buyoutDeviceModalPurchaseAnywayTitle: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.title'),
    buyoutDeviceModalPurchaseAnywayText1: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph1'),
    buyoutDeviceModalPurchaseAnywayText2: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph2'),
    buyoutDeviceModalPurchaseAnywayText3: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.paragraph3'),
    buyoutDeviceModalPurchaseAnywayConfirmButton: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.confirmButton'),
    buyoutDeviceModalPurchaseAnywayCancelButton: t('AssetCard.purchaseWithPriceDeviceModals.purchaseAssetAnywayModal.content.cancelButton'),
    buyoutDeviceModalNoPriceAvailableTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.title'),
    buyoutDeviceModalNoPriceAvailableDescription: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.description'),
    buyoutDeviceModalNoPriceAvailableOptionsTitle: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionsTitle'),
    buyoutDeviceModalNoPriceAvailableOptionWait: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionWaitReusable'),
    buyoutDeviceModalNoPriceAvailableOptionPurchaseAnyway: t(
      'AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionPurchaseAnyway',
    ),
    buyoutDeviceModalNoPriceAvailableOptionCancel: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.optionCancel'),
    buyoutDeviceModalNoPriceAvailableCancelButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.cancelButton'),
    buyoutDeviceModalNoPriceAvailableWaitButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.waitButton'),
    buyoutDeviceModalNoPriceAvailablePurchaseButton: t('AssetCard.purchaseWithPriceDeviceModals.priceNotAvailableModal.content.purchaseButton'),
    buyoutDeviceModalPriceAvailableTitle_1: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.titleReusable_1'),
    buyoutDeviceModalPriceAvailableTitle_2: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.titleReusable_2'),
    buyoutDeviceModalPriceAvailableText1: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph1'),
    buyoutDeviceModalPriceAvailableText2: t('AssetCard.purchaseWithPriceDeviceModals.priceAvailableModal.generalInfo.paragraph2'),
    buyoutDeviceModalPriceAvailableCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalPriceAvailableConfirmButton: t('AssetCard.confirmButton'),
    buyoutDeviceModalWithoutPriceTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.title'),
    buyoutDeviceModalWithoutPriceText1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part1'),
    buyoutDeviceModalWithoutPriceText2_1: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_1'),
    buyoutDeviceModalWithoutPriceText2_2: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.part2Reusable_2'),
    buyoutDeviceModalWithoutPriceConfirmButton: t('AssetCard.purchaseWithoutPriceDeviceModals.priceNotAvailable.confirmButton'),
    buyoutDeviceModalWithoutPriceCancelButton: t('AssetCard.cancelButton'),
    buyoutDeviceModalWithoutPriceConfirmedTitle: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedTitle'),
    buyoutDeviceModalWithoutPriceConfirmedText: t('AssetCard.purchaseWithoutPriceDeviceModals.confirmedText'),
    buyoutDeviceModalWithoutPriceConfirmedButton: t('AssetCard.okButton'),
    upgradeDeviceOptionText: t('AssetCard.assetActions.upgradeDeviceText'),
    upgradeDeviceModalTitle: t('AssetCard.upgradeDeviceModals.title'),
    upgradeDeviceModalText: t('AssetCard.upgradeDeviceModals.text'),
    upgradeDeviceModalNextSteps: t('AssetCard.upgradeDeviceModals.nextSteps'),
    upgradeDeviceModalStep1: t('AssetCard.upgradeDeviceModals.step1'),
    upgradeDeviceModalStep2: t('AssetCard.upgradeDeviceModals.step2'),
    upgradeDeviceModalContinueButton: t('AssetCard.continueButton'),
    upgradeDeviceModalCancelButton: t('AssetCard.cancelButton'),
    extendDeviceOptionTitle: t('AssetCard.assetActions.extendDeviceOption'),
    extendDeviceOptionTextStart: t('AssetCard.assetActions.extendDeviceTextReusableStart'),
    extendDeviceOptionTextEnd: t('AssetCard.assetActions.extendDeviceTextReusableEnd'),
    extendDeviceOptionButton: t('AssetCard.assetActions.extendDeviceButton'),
    extendDeviceModalsTitle: t('AssetCard.extendDeviceModals.title'),
    extendDeviceModalsText: t('AssetCard.extendDeviceModals.text'),
    extendDeviceModalsOptionTitle: t('AssetCard.extendDeviceModals.optionTitle'),
    extendDeviceModalsMonths: t('AssetCard.months'),
    extendDeviceModalsConfirmSelectionTitle: t('AssetCard.extendDeviceModals.confirmSelectionTitle'),
    extendDeviceModalsConfirmSelectionText: t('AssetCard.extendDeviceModals.confirmSelectionText'),
    extendDeviceModalsConfirmedTitle: t('AssetCard.extendDeviceModals.confirmedTitle'),
    extendDeviceModalsNextButton: t('AssetCard.nextButton'),
    extendDeviceModalsConfirmButton: t('AssetCard.confirmButton'),
    extendDeviceModalsCancelButton: t('AssetCard.cancelButton'),
    extendDeviceModalsOkButton: t('AssetCard.okButton'),
    extendDeviceOrSeparator: t('AssetCard.extendDeviceModals.orSeparator'),
    returnDeviceOptionTitle: t('AssetCard.assetActions.returnDeviceOption'),
    returnDeviceOptionText: t('AssetCard.assetActions.returnDeviceText'),
    returnDeviceOptionButton: t('AssetCard.assetActions.returnDeviceButton'),
    returnDeviceModalTitle: t('AssetCard.returnDeviceModals.title'),
    returnDeviceModalContinueButton: t('AssetCard.continueButton'),
    returnDeviceModalCancelButton: t('AssetCard.cancelButton'),
    returnDeviceModalText1: t('AssetCard.returnDeviceModals.text1'),
    returnDeviceModalText2: t('AssetCard.returnDeviceModals.text2'),
    returnDeviceReturnableDeviceTitle: t('ReturnAsset.returnReturnableDevice.title'),
    returnDeviceReturnableDeviceText1: t('ReturnAsset.returnReturnableDevice.text1'),
    returnDeviceReturnableDeviceText2: t('ReturnAsset.returnReturnableDevice.text2'),
    returnDeviceReturnConfirmedHeadline: t('ReturnAsset.returnConfirmed.headline'),
    returnDeviceReturnConfirmedTitle: t('ReturnAsset.returnConfirmed.title'),
    returnDeviceReturnConfirmedUpgradeTitle: t('ReturnAsset.returnConfirmed.upgradeTitle'),
    returnDeviceReturnConfirmedText1: t('ReturnAsset.returnConfirmed.text'),
    returnDeviceReturnConfirmedText2: t('ReturnAsset.orderReturnEnvelope.deliveryText'),
    returnDeviceReturnConfirmedDelivered: t('ReturnAsset.returnConfirmed.delivered'),
    returnDeviceReturnConfirmedServiceDescription: t('ReturnAsset.returnConfirmed.serviceDescription'),
    returnDeviceReturnConfirmedNextButton: t('ReturnAsset.returnConfirmed.nextButton'),
    returnDeviceReturnConfirmedUpgradeButton: t('ReturnAsset.returnConfirmed.upgradeNextButton'),
    returnDeviceReturnConfirmedOptionDelivered: t('ReturnAsset.returnConfirmed.optionDelivered'),
    returnDeviceReturnConfirmedDeliveryEstimateNotAvailable: t('ReturnAsset.returnConfirmed.deliveryEstimateNotAvailable'),
    returnDeviceOrderReturnHeadline: t('ReturnAsset.orderReturnEnvelope.headline'),
    returnDeviceOrderReturnEmployeeTitle: tShop('checkout.detailsPage.employeeTitle'),
    returnDeviceOrderReturnDeliveryTitle: tShop('checkout.detailsPage.deliveryTitle'),
    returnDeviceOrderReturnDeliveryText: t('ReturnAsset.orderReturnEnvelope.deliveryText'),
    returnDeviceOrderReturnDeliveryQuestion: tShop('checkout.detailsPage.deliveryQuestion'),
    returnDeviceOrderReturnFirstName: tShop('checkout.detailsPage.form.firstName'),
    returnDeviceOrderReturnLastName: tShop('checkout.detailsPage.form.lastName'),
    returnDeviceOrderReturnEmail: tShop('checkout.detailsPage.form.email'),
    returnDeviceOrderReturnPhoneNumber: tShop('checkout.detailsPage.form.phoneNbr'),
    returnDeviceOrderReturnAddress1: tShop('checkout.detailsPage.form.address1'),
    returnDeviceOrderReturnAddress2: tShop('checkout.detailsPage.form.address2'),
    returnDeviceOrderReturnStreet: tShop('checkout.detailsPage.form.streetAddress'),
    returnDeviceOrderReturnCity: tShop('checkout.detailsPage.form.city'),
    returnDeviceOrderReturnPostalCode: tShop('checkout.detailsPage.form.postalCode'),
    returnDeviceOrderReturnCountry: tShop('checkout.detailsPage.form.country'),
    returnDeviceOrderReturnCountryPlaceholder: tShop('checkout.detailsPage.form.countryPlaceholder'),
    returnDeviceOrderReturnHomeDeliveryOption: tShop('checkout.detailsPage.form.homeDeliveryOption'),
    returnDeviceOrderReturnHomeDeliveryOptionDetails: tShop('checkout.detailsPage.form.homeDeliveryOptionDetails'),
    returnDeviceOrderReturnCompanyDeliveryOption: tShop('checkout.detailsPage.form.companyDeliveryOption'),
    returnDeviceOrderReturnCompanyDeliveryOptionDetails: tShop('checkout.detailsPage.form.companyDeliveryOptionDetails'),
    returnDeviceOrderReturnRequired: tShop('checkout.detailsPage.form.rules.required'),
    returnDeviceOrderReturnInvalidEmail: tShop('checkout.detailsPage.form.rules.invalidEmail'),
    returnDeviceOrderReturnInvalidPhoneNumber: tShop('checkout.detailsPage.form.rules.invalidPhoneNumber'),
    returnDeviceOrderReturnInvalidPostalCode: tShop('checkout.detailsPage.form.rules.invalidPostalCode'),
    returnDeviceOrderReturnNextButton: t('ReturnAsset.orderReturnEnvelope.confirmButton'),
    returnDeviceOrderReturnCancelButton: t('ReturnAsset.orderReturnEnvelope.cancelButton'),
    returnDeviceOrderReturnFinland: tGlobal('countries.Finland'),
    returnDeviceOrderReturnSweden: tGlobal('countries.Sweden'),
    returnDeviceOrderReturnNorway: tGlobal('countries.Norway'),
    returnDeviceOrderReturnDenmark: tGlobal('countries.Denmark'),
    returnDeviceOrderReturnUnitedKingdom: tGlobal('countries.UnitedKingdom'),
  };

  useEffect(() => {
    if (assetUser) {
      getUsersAllAssetsApi(assetUser.id).then(assets => {
        if (assets) {
          setUserAssets(assets);
        }
      });
    } else {
      setUserAssets([]);
    }
  }, [assetUser]);

  const getUser = async (userId: string) => {
    const user = await getAUser(userId);
    if (user) setAssetUser(user);
  };

  const getOrganizationAsset = async (assetId: string): Promise<void> => {
    if (!asset) {
      setLoading(true);
    }
    const response = await getAssetApi(assetId);
    if (response) {
      setAsset(response);
      // Get user for end of lease choice
      if (response.end_of_lease_options && response.end_user.user?.id) {
        getUser(response.end_user.user.id);
      }
    } else {
      dispatch(showGrowlNotification(t('getOrganizationDevicesError'), 'error'));
      setAsset(undefined);
    }
    setLoading(false);
  };

  const getOrganizationInvoicingAccounts = async () => {
    setInvoicingAccountsLoading(true);
    const res = await getInvoicingAccounts();
    res ? setInvoicingAccounts(res) : dispatch(showGrowlNotification(tErrors('invoicingAccount.getAllInvoicingAccountsError'), 'error'));
    setInvoicingAccountsLoading(false);
  };

  useEffect(() => {
    getOrganizationAsset(assetId);
    getOrganizationInvoicingAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateTicket = () => {
    const url = getTicketURI(organization?.locale.language);
    createTicket(url, asset);
  };

  const handleAssignToDifferentUserInitialization = async () => {
    const getOrganizationAllUsers = async () => {
      setOrganizationUsersLoading(true);
      const response = await getUsersFromOrganizationOrUserGroup('organization');
      if (response) {
        setOrganizationUsers(response);
        setAssignToDifferentUserModalOpen(true);
      } else {
        dispatch(showGrowlNotification(t('AssetCard.assign.errorFetchingOrganizationUsers'), 'error'));
      }
      setOrganizationUsersLoading(false);
    };
    // If user opens and closes the asset assign modal multiple times, get users from backend only on the first time
    if (organizationUsers.length === 0) {
      getOrganizationAllUsers();
    } else {
      setAssignToDifferentUserModalOpen(true);
    }
  };

  const handleTargetUserSelected = async (user: User): Promise<UserDetails> => {
    const response = await getAUser(user?.id || '', user?.user_group?.id || '');
    if (response) {
      return response;
    } else {
      // Reusable AssetAssignToUser component assumes this callback to throw if target user details fetching fails
      throw new Error('Fetching target user details failed');
    }
  };

  const handleAssignToDifferentUser = async (user: UserDetails, invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success;
    const assignedAsset = await assignAssetToUser(asset?.id || '', user.id, invoicingAccountId);
    if (assignedAsset) {
      dispatch(showGrowlNotification(t('AssetCard.assign.assetAssignSuccessfulUser', { targetUser: user.name || '' }), 'success'));
      setAssignToDifferentUserModalOpen(false);
      setAsset(assignedAsset);
      getUser(assignedAsset.end_user.user?.id || '');
      success = true;
    } else {
      success = false;
    }
    setAssetAssignLoading(false);
    if (!success) {
      getOrganizationAsset(assetId);
    }
    return success;
  };

  const handleAssignToOrganization = async (invoicingAccountId?: string): Promise<boolean> => {
    setAssetAssignLoading(true);
    let success = false;
    const assignedAsset = await assignAssetToOrganization(asset?.id || '', invoicingAccountId);
    if (assignedAsset) {
      dispatch(
        showGrowlNotification(t('AssetCard.assign.assetAssignSuccessfulOrganization', { targetOrganization: organization?.name || '' }), 'success'),
      );
      setAssignToOrganizationModalOpen(false);
      setAsset(assignedAsset);
      setAssetUser(undefined);
      success = true;
    }
    setAssetAssignLoading(false);
    if (!success) {
      getOrganizationAsset(assetId);
    }
    return success;
  };

  const getCurrentUserLink = (): string | undefined => {
    const currentUser = organizationUsers.find(orgUser => orgUser?.id && orgUser?.id === asset?.end_user.user?.id);
    if (currentUser?.id && currentUser?.user_group?.id) {
      return `/users/usergroup/${currentUser?.user_group?.id}/user/${currentUser?.id}`;
    }
    return undefined;
  };
  const currentUserLink = getCurrentUserLink();

  const handleUpdateLocationReference = async (newLocationReference: string) => {
    if (asset) {
      const updatedAsset = await updateOrganizationAsset(asset.id, {
        location_reference: newLocationReference || null,
        invoicing_account: asset.invoicing_account?.id,
      });
      if (updatedAsset) {
        setAsset(updatedAsset);
      } else {
        dispatch(showGrowlNotification(tGlobal('TopErrorBoundary.title'), 'error'));
      }
    }
  };

  const handleUpdateInvoicingAccount = async (invoicingAccount?: InvoicingAccountOptionResponseContents | InvoicingAccountResponse | undefined) => {
    if (asset) {
      const updatedAsset = await updateOrganizationAsset(asset.id, {
        location_reference: asset.location_reference,
        invoicing_account: invoicingAccount?.id || null,
      });
      if (updatedAsset) {
        setAsset(updatedAsset);
      } else {
        dispatch(showGrowlNotification(t('AssetCard.invoicingAccountSaveError'), 'error'));
      }
    }
  };

  const handleCreateDraftDecisionRequest = async () => {
    const res = await postEndOfLeaseDecision(assetId, 'admin', 'buyout', undefined, undefined, false);
    if (!res?.unconfirmed_end_of_lease_decisions?.length) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionAskForBuyoutPricePostError'), 'error'));
      return undefined;
    }
    return res.unconfirmed_end_of_lease_decisions;
  };

  const handleConfirmDecisionRequest = async (request: AssetEndOfLeaseRequest) => {
    // Handle eol return decision
    if (request.return) {
      const assetResponse = await postEndOfLeaseDecision(assetId, 'admin', 'return', request.return.from, undefined, request.confirm);
      if (assetResponse) {
        const returnOrder = assetResponse.prices.lease?.return_package?.order_id
          ? await getReturnPackageOrder(assetResponse.prices.lease.return_package.order_id)
          : null;
        // We don't care if getReturnPackageOrder fails. We use only minor information from response.
        // Order service needs to support this functionality for child organizations.
        // This will probably support after Order on behalf for child organizations is implemented.
        return { success: true, orderDeliveryUpdatesForReturn: returnOrder?.delivery_updates };
      }
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
      return { success: false };
    } else {
      // Handle other than return eol decision
      const res = await postEndOfLeaseDecision(
        assetId,
        'admin',
        request.buyout ? 'buyout' : 'extension',
        undefined,
        request.extension ? request.extension.length : undefined,
        request.confirm,
      );
      if (res) {
        return { success: true, orderDeliveryUpdatesForReturn: undefined };
      } else {
        dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
        return { success: false };
      }
    }
  };

  const handleConfirmOrCancelDraftDecisionRequest = async (eolId: string, confirm: boolean) => {
    const res = await confirmOrCancelEndOfLeaseDecision(assetId, eolId, confirm, 'admin');
    if (!res && !confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionBuyoutCancelError'), 'error'));
    } else if (!res && confirm) {
      dispatch(showGrowlNotification(t('AssetCard.eolDecisionPostError'), 'error'));
    } else if (res && !confirm) {
      setAsset(res as OrganizationAssetResponse);
      dispatch(showGrowlNotification(t('AssetCard.endOfLeaseNotifications.priceRequestCancelled'), 'warning'));
    }
    return !!res;
  };

  const handleEndOfLeaseFlowFinish = (selectedChoice: EolFlowFinishFromChoice) => {
    dispatch(showGrowlNotification(t('AssetCard.eolDecisionMade'), 'success'));
    switch (selectedChoice) {
      case 'upgrade':
        if (asset) {
          dispatch(refreshUserInfo()).then(() => {
            if (!asset.end_user.user) {
              dispatch(shopSetOrderForOrganization());
              history.push('/home');
            } else if (asset.end_user.user.id !== loggedInUser?.id) {
              getAUser(asset.end_user.user.id).then(endUser => {
                if (endUser) {
                  dispatch(shopSetOrderOnBehalfUser(endUser));
                }
                history.push('/home');
              });
            } else {
              history.push('/home');
            }
          });
        }
        break;
      case 'return':
        dispatch(refreshUserInfo());
        getOrganizationAsset(assetId);
        break;
      case 'extension':
        getOrganizationAsset(assetId);
        break;
      case 'buyout':
        dispatch(refreshUserInfo());
        getOrganizationAsset(assetId);
        break;
      default:
        getOrganizationAsset(assetId);
        break;
    }
  };

  const isAssignableToOrganization = (asset: OrganizationAssetResponse) => asset.assign_user_allowed && asset.end_user.user;

  const renderAssetCardOrNotFound = (asset: OrganizationAssetResponse | undefined, id: string) => {
    if (asset) {
      return (
        <>
          <OrganizationAssetCard
            asset={asset}
            userAssets={userAssets}
            isOpen={true}
            handleCreateTicket={handleCreateTicket}
            customSecondTicketModalContent={renderContactSupportText(t('AssetCard.secondTicketModal.content'))}
            translations={translations}
            currency={organization?.locale.currency || ''}
            endOfLeaseOptions={{
              translationsForEndOfLeaseChoice: endOfLeaseChoiceTranslations,
              handleCreateDraftDecisionRequest: handleCreateDraftDecisionRequest,
              handleConfirmDecisionRequest: handleConfirmDecisionRequest,
              handleConfirmOrCancelDraftDecisionRequest: handleConfirmOrCancelDraftDecisionRequest,
              handleEndOfLeaseFlowFinish: handleEndOfLeaseFlowFinish,
              buyoutWithPriceFlow: useBuyoutWithPriceFlow,
              showUpgrade: hasUserARequiredRole([UserRole.Orderer]),
              user: assetUser,
              // Allow return only for Norway addresses
              countries: [COUNTRY_CALLING_CODES.find(country => country.value === 'NO')!],
            }}
            handleAssignToDifferentUser={asset.assign_user_allowed ? handleAssignToDifferentUserInitialization : undefined}
            handleAssignToOrganization={isAssignableToOrganization(asset) ? () => setAssignToOrganizationModalOpen(true) : undefined}
            handleSaveLocationReference={handleUpdateLocationReference}
            fetchInvoicingAccounts={() => Promise.resolve(invoicingAccounts)}
            handleSaveInvoicingAccount={handleUpdateInvoicingAccount}
          />
          {organizationUsersLoading && <CustomLoader centered={true} />}
          {assetAssignLoading && <CustomLoader centered={true} dimBackground={true} />}
          {assignToDifferentUserModalOpen && (
            <AssetAssignToUser
              translations={assignToUserTranslations}
              assetToTransfer={asset}
              possibleTargetUsers={organizationUsers}
              onTargetUserSelect={handleTargetUserSelected}
              onSave={handleAssignToDifferentUser}
              onCancel={() => setAssignToDifferentUserModalOpen(false)}
              showConfirmation={true}
              invoicingAccounts={invoicingAccounts}
              getCurrentUserLink={currentUserLink ? () => currentUserLink : undefined}
              getTargetUserLink={user => `/users/usergroup/${user.user_group.id}/user/${user.id}`}
              currency={organization?.locale.currency || ''}
            />
          )}
          {organization && assignToOrganizationModalOpen && (
            <AssetAssignToOrganization
              translations={assignToOrganizationTranslations}
              asset={asset}
              targetOrganization={organization}
              onSave={handleAssignToOrganization}
              onCancel={() => setAssignToOrganizationModalOpen(false)}
              showConfirmation={true}
              invoicingAccounts={invoicingAccounts}
              getCurrentUserLink={currentUserLink ? () => currentUserLink : undefined}
              currency={organization?.locale.currency || ''}
            />
          )}
        </>
      );
    }
    return (
      <Title color={'black'} size={'md'}>
        {t('OrganizationAssets.assetNotFound')} {id}
      </Title>
    );
  };

  return (
    <>
      <Div
        style={{ display: 'inline-flex' }}
        margin={{ top: 'md', bottom: 'lg' }}
        onClick={() => history.push('/organizationassets')}
        clickableAppearance
      >
        <Icon margin={{ top: 'xxs', right: 'xs' }} color={'purple500'} size={'sm'} name={'chevron-left'}></Icon>
        <BodyText color={'purple500'}>{t('OrganizationAssets.goBackToDevices')}</BodyText>
      </Div>
      <Headline size={'lg'} margin={{ bottom: 'xlg' }}>
        {t('OrganizationAssets.deviceInformationTitle')}
      </Headline>
      {loading || invoicingAccountsLoading ? <CustomLoader /> : renderAssetCardOrNotFound(asset, assetId)}
    </>
  );
};

export default OrganizationAssetDetails;
